import React, { useState, useEffect, useMemo } from 'react';
import { ShoppingCart, Filter, Plus, Minus, ArrowRight, Truck, Package, ChevronDown, List } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { getProducts, getCategories, getProductImageUrl } from '../lib/api';
import { Toaster, toast } from 'sonner';
import SaveAsListModal from '../components/SaveAsListModal';

const DISCOUNT_TIERS = [
  { min: 10, pct: 0.02 },
  { min: 50, pct: 0.04 },
  { min: 100, pct: 0.06 },
];

const getDiscount = (units) => {
  if (units >= 100) return 0.06;
  if (units >= 50) return 0.04;
  if (units >= 10) return 0.02;
  return 0;
};

const getNextTier = (units) => {
  for (const tier of DISCOUNT_TIERS) {
    if (units < tier.min) return tier;
  }
  return null;
};

export const BulkOrderBuilderPage = () => {
  const { t, language, addToRequest, requestList, isBuyerApproved, isAdmin } = useApp();
  const canSeePrice = isBuyerApproved || isAdmin;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [catFilter, setCatFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  // Selection: { productId: quantity }
  const [selected, setSelected] = useState({});
  const [showSaveListModal, setShowSaveListModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          getProducts({ limit: 500 }),
          getCategories(),
        ]);
        setProducts(pRes.data || []);
        setCategories(cRes.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const brands = useMemo(() => [...new Set(products.map(p => p.oem).filter(Boolean))].sort(), [products]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (catFilter && p.category !== catFilter) return false;
      if (brandFilter && p.oem !== brandFilter) return false;
      if (gradeFilter && !p.grade?.includes(gradeFilter)) return false;
      if (canSeePrice && minPrice && p.sale_price < Number(minPrice)) return false;
      if (canSeePrice && maxPrice && p.sale_price > Number(maxPrice)) return false;
      return true;
    });
  }, [products, catFilter, brandFilter, gradeFilter, minPrice, maxPrice, canSeePrice]);

  const setQty = (pid, qty) => {
    setSelected(prev => {
      const next = { ...prev };
      if (qty <= 0) {
        delete next[pid];
      } else {
        next[pid] = qty;
      }
      return next;
    });
  };

  const totalUnitsSelected = Object.values(selected).reduce((s, q) => s + q, 0);
  const discountPct = getDiscount(totalUnitsSelected);
  const nextTier = getNextTier(totalUnitsSelected);

  const estimatedTotal = useMemo(() => {
    return Object.entries(selected).reduce((sum, [pid, qty]) => {
      const prod = products.find(p => p.id === pid);
      if (!prod || !prod.sale_price) return sum;
      const unitPrice = Math.round(prod.sale_price * (1 - discountPct));
      return sum + unitPrice * qty;
    }, 0);
  }, [selected, products, discountPct]);

  const selectedCount = Object.keys(selected).length;

  const handleAddToCart = () => {
    let added = 0;
    Object.entries(selected).forEach(([pid, qty]) => {
      const prod = products.find(p => p.id === pid);
      if (!prod) return;
      const already = requestList.find(r => r.product_id === pid);
      if (!already) {
        addToRequest({
          product_id: prod.id,
          unique_id: prod.unique_id,
          oem: prod.oem,
          model: prod.model,
          sale_price: prod.sale_price,
          quantity: qty,
        });
        added++;
      }
    });
    if (added > 0) {
      toast.success(language === 'es' ? `${added} productos agregados al carrito` : `${added} products added to cart`);
      setSelected({});
    }
  };

  if (!canSeePrice) {
    return (
      <div className="min-h-screen bg-[#050505] pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center py-20">
          <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{language === 'es' ? 'Acceso Restringido' : 'Restricted Access'}</h2>
          <p className="text-white/50">{language === 'es' ? 'Inicie sesión como comprador aprobado para usar el constructor de pedidos.' : 'Log in as an approved buyer to use the Bulk Order Builder.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-16" data-testid="bulk-order-builder">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            {language === 'es' ? 'Constructor de Pedidos' : 'Bulk Order Builder'}
          </h1>
          <p className="text-white/50 text-sm">
            {language === 'es'
              ? 'Seleccione productos y cantidades para alcanzar mejores descuentos por volumen.'
              : 'Select products and quantities to reach better volume discount tiers.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* Main content: filters + grid */}
          <div className="lg:col-span-3">

            {/* Filters */}
            <div className="card-dark p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-white/40" />
                <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  {language === 'es' ? 'Filtros' : 'Filters'}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm text-white" data-testid="bob-filter-category">
                  <option value="">{language === 'es' ? 'Categoría' : 'Category'}</option>
                  {categories.map(c => <option key={c.name || c} value={c.name || c}>{c.name || c}</option>)}
                </select>
                <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)} className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm text-white" data-testid="bob-filter-brand">
                  <option value="">{language === 'es' ? 'Marca' : 'Brand'}</option>
                  {brands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)} className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm text-white" data-testid="bob-filter-grade">
                  <option value="">{language === 'es' ? 'Grado' : 'Grade'}</option>
                  <option value="A+">A+</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
                <input type="number" placeholder={language === 'es' ? 'Precio mín' : 'Min price'} value={minPrice} onChange={e => setMinPrice(e.target.value)} className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm text-white font-mono" data-testid="bob-filter-min-price" />
                <input type="number" placeholder={language === 'es' ? 'Precio máx' : 'Max price'} value={maxPrice} onChange={e => setMaxPrice(e.target.value)} className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm text-white font-mono" data-testid="bob-filter-max-price" />
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="card-dark p-12 text-center text-white/40 animate-pulse">
                {language === 'es' ? 'Cargando productos...' : 'Loading products...'}
              </div>
            ) : filtered.length === 0 ? (
              <div className="card-dark p-12 text-center text-white/40">
                {language === 'es' ? 'No se encontraron productos.' : 'No products found.'}
              </div>
            ) : (
              <div className="card-dark overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#0A0A0A] border-b border-white/10">
                        <th className="px-3 py-3 text-left text-[11px] font-semibold text-white/60 uppercase w-16">{language === 'es' ? 'Cant.' : 'Qty'}</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold text-white/60 uppercase">SKU</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold text-white/60 uppercase">{language === 'es' ? 'Producto' : 'Product'}</th>
                        <th className="px-3 py-3 text-center text-[11px] font-semibold text-white/60 uppercase">{language === 'es' ? 'Grado' : 'Grade'}</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold text-white/60 uppercase">CPU</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold text-white/60 uppercase">RAM</th>
                        <th className="px-3 py-3 text-right text-[11px] font-semibold text-white/60 uppercase">{language === 'es' ? 'Precio' : 'Price'}</th>
                        <th className="px-3 py-3 text-right text-[11px] font-semibold text-white/60 uppercase">{language === 'es' ? 'Subtotal' : 'Subtotal'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(product => {
                        const qty = selected[product.id] || 0;
                        const unitPrice = product.sale_price ? Math.round(product.sale_price * (1 - discountPct)) : 0;
                        const lineTotal = unitPrice * qty;
                        return (
                          <tr key={product.id} className={`border-b border-white/5 ${qty > 0 ? 'bg-[#00D4FF]/5' : 'hover:bg-white/[0.02]'}`} data-testid={`bob-row-${product.id}`}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setQty(product.id, Math.max(0, qty - 1))}
                                  className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                                  data-testid={`bob-minus-${product.id}`}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  value={qty || ''}
                                  onChange={e => setQty(product.id, Math.max(0, parseInt(e.target.value) || 0))}
                                  placeholder="0"
                                  className="w-12 px-1 py-1 bg-transparent border border-white/10 text-center text-white font-mono text-sm focus:border-[#00D4FF] outline-none"
                                  data-testid={`bob-qty-${product.id}`}
                                />
                                <button
                                  onClick={() => setQty(product.id, qty + 1)}
                                  className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-[#00D4FF] border border-white/10 hover:border-[#00D4FF]/30 transition-colors"
                                  data-testid={`bob-plus-${product.id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {product.image_url ? (
                                  <img src={getProductImageUrl(product.id)} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" />
                                ) : null}
                                <span className="font-mono text-[#00D4FF] text-xs">{product.unique_id}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-white text-xs font-medium">{product.oem} {product.model}</div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {product.grade && (
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white ${
                                  product.grade?.includes('A') ? 'bg-[#10B981]' : product.grade?.includes('B') ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'
                                }`}>{product.grade}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-white/60 text-[11px] font-mono">{product.cpu || '-'}</td>
                            <td className="px-3 py-2 text-white/60 text-xs font-mono">{product.ram || '-'}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="font-mono text-sm text-white">${unitPrice.toLocaleString()}</div>
                              {discountPct > 0 && (
                                <div className="text-[10px] text-white/30 line-through">${product.sale_price?.toLocaleString()}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-sm">
                              {qty > 0 ? (
                                <span className="text-[#00D4FF] font-bold">${lineTotal.toLocaleString()}</span>
                              ) : (
                                <span className="text-white/20">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar: Running totals */}
          <div className="lg:col-span-1">
            <div className="card-dark p-5 sticky top-24">
              <h3 className="text-base font-semibold mb-5">{language === 'es' ? 'Resumen del Pedido' : 'Order Summary'}</h3>

              {/* Stats */}
              <div className="space-y-3 mb-5">
                <div className="flex justify-between text-sm text-white/60">
                  <span>{language === 'es' ? 'SKUs seleccionados' : 'SKUs selected'}</span>
                  <span className="font-mono font-bold text-white">{selectedCount}</span>
                </div>
                <div className="flex justify-between text-sm text-white/60">
                  <span>{language === 'es' ? 'Total unidades' : 'Total units'}</span>
                  <span className="font-mono font-bold text-white">{totalUnitsSelected}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">{language === 'es' ? 'Descuento' : 'Discount'}</span>
                  <span className={`font-mono font-bold ${discountPct > 0 ? 'text-[#10B981]' : 'text-white/30'}`}>
                    {discountPct > 0 ? `-${(discountPct * 100).toFixed(0)}%` : '0%'}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-bold">
                  <span>{language === 'es' ? 'Estimado' : 'Estimated'}</span>
                  <span className="font-mono gradient-text">${estimatedTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Discount Progress */}
              <div className="bg-[#050505] border border-white/10 p-3 mb-5" data-testid="bob-discount-progress">
                {totalUnitsSelected === 0 ? (
                  <p className="text-white/40 text-xs">{language === 'es' ? 'Agregue productos para comenzar' : 'Add products to get started'}</p>
                ) : nextTier ? (
                  <p className={`text-xs ${discountPct > 0 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                    {discountPct > 0
                      ? (language === 'es'
                        ? `${(discountPct * 100).toFixed(0)}% descuento aplicado. Agregue ${nextTier.min - totalUnitsSelected} más para ${(nextTier.pct * 100).toFixed(0)}%`
                        : `${(discountPct * 100).toFixed(0)}% discount applied. Add ${nextTier.min - totalUnitsSelected} more for ${(nextTier.pct * 100).toFixed(0)}%`)
                      : (language === 'es'
                        ? `Agregue ${nextTier.min - totalUnitsSelected} unidades más para desbloquear ${(nextTier.pct * 100).toFixed(0)}% descuento`
                        : `Add ${nextTier.min - totalUnitsSelected} more units to unlock ${(nextTier.pct * 100).toFixed(0)}% discount`)
                    }
                  </p>
                ) : (
                  <p className="text-[#10B981] text-xs font-semibold">
                    {language === 'es' ? '6% descuento máximo aplicado' : '6% max discount applied'}
                  </p>
                )}
              </div>

              {/* Discount Tiers Reference */}
              <div className="mb-5">
                <div className="text-[10px] text-white/30 uppercase tracking-wider mb-2">{language === 'es' ? 'Niveles de Descuento' : 'Discount Tiers'}</div>
                <div className="space-y-1">
                  {DISCOUNT_TIERS.map(tier => (
                    <div key={tier.min} className={`flex justify-between text-xs px-2 py-1 ${totalUnitsSelected >= tier.min ? 'bg-[#10B981]/10 text-[#10B981]' : 'text-white/30'}`}>
                      <span>{tier.min}+ {language === 'es' ? 'uds' : 'units'}</span>
                      <span className="font-mono font-bold">{(tier.pct * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FOB Notice */}
              <div className="flex items-start gap-2 p-3 bg-[#10B981]/5 border border-[#10B981]/20 mb-5" data-testid="fob-notice-bob">
                <Truck className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
                <p className="text-[#10B981]/80 text-[10px]">
                  {language === 'es'
                    ? 'Precios FOB Miami. El comprador gestiona envío y aduanas.'
                    : 'Prices are FOB Miami. Buyer arranges shipping and customs.'}
                </p>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={selectedCount === 0}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="bob-add-to-cart"
              >
                <ShoppingCart className="w-4 h-4" />
                {language === 'es'
                  ? `Agregar ${selectedCount > 0 ? selectedCount : ''} al Carrito`
                  : `Add ${selectedCount > 0 ? selectedCount : ''} to Cart`}
              </button>

              <button
                onClick={() => setShowSaveListModal(true)}
                disabled={selectedCount === 0}
                className="w-full flex items-center justify-center gap-2 mt-3 px-4 py-2.5 text-sm font-medium border border-white/10 text-white/60 hover:text-[#00D4FF] hover:border-[#00D4FF]/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="bob-save-as-list"
              >
                <List className="w-4 h-4" />
                {language === 'es' ? 'Guardar como Lista' : 'Save as List'}
              </button>
            </div>
          </div>
        </div>

        <div className="h-16" />
      </div>

      {showSaveListModal && selectedCount > 0 && (
        <SaveAsListModal
          items={Object.entries(selected).map(([pid, qty]) => ({ product_id: pid, qty }))}
          onClose={() => setShowSaveListModal(false)}
          onSaved={() => setSelected({})}
        />
      )}
    </div>
  );
};
